import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ArrowUpRight, 
  ArrowDownRight,
  BarChart3,
  PieChart,
  RefreshCw,
  Download,
  Calendar,
  Wallet,
  Receipt,
  Car,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ProfitLoss() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [plData, setPlData] = useState(null);
  const [trendData, setTrendData] = useState([]);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plRes, trendRes] = await Promise.all([
        axios.get(`${API_URL}/api/business/profit-loss`, { 
          params: { period },
          withCredentials: true 
        }),
        axios.get(`${API_URL}/api/business/profit-loss/monthly-trend`, { 
          params: { months: 6 },
          withCredentials: true 
        })
      ]);
      
      setPlData(plRes.data);
      setTrendData(trendRes.data.monthly_data || []);
    } catch (error) {
      console.error('Error fetching P&L data:', error);
      toast.error('Failed to load profit & loss data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(1) || 0}%`;
  };

  // Calculate max value for chart scaling
  const maxChartValue = Math.max(
    ...trendData.map(d => Math.max(d.income || 0, d.expenses || 0)),
    1
  );

  if (loading && !plData) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading P&L data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="profit-loss-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="pl-title">Profit & Loss</h1>
          <p className="text-muted-foreground mt-1">
            Real-time financial performance overview
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[160px]" data-testid="period-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
              <SelectItem value="year">Full Year</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Income */}
        <Card className="border-emerald-500/20 bg-emerald-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Income</p>
                <p className="text-2xl font-bold text-emerald-400" data-testid="total-income">
                  {formatCurrency(plData?.income?.total)}
                </p>
                <div className={`flex items-center text-sm mt-1 ${
                  (plData?.income?.change_percent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(plData?.income?.change_percent || 0) >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                  )}
                  {formatPercent(plData?.income?.change_percent)} vs last period
                </div>
              </div>
              <div className="p-3 rounded-full bg-emerald-500/10">
                <TrendingUp className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Expenses */}
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-bold text-red-400" data-testid="total-expenses">
                  {formatCurrency(plData?.expenses?.total)}
                </p>
                <div className={`flex items-center text-sm mt-1 ${
                  (plData?.expenses?.change_percent || 0) <= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(plData?.expenses?.change_percent || 0) <= 0 ? (
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                  )}
                  {formatPercent(plData?.expenses?.change_percent)} vs last period
                </div>
              </div>
              <div className="p-3 rounded-full bg-red-500/10">
                <TrendingDown className="w-6 h-6 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card className={`border-${(plData?.profit?.net || 0) >= 0 ? 'blue' : 'orange'}-500/20 bg-${(plData?.profit?.net || 0) >= 0 ? 'blue' : 'orange'}-500/5`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className={`text-2xl font-bold ${(plData?.profit?.net || 0) >= 0 ? 'text-blue-400' : 'text-orange-400'}`} data-testid="net-profit">
                  {formatCurrency(plData?.profit?.net)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {plData?.profit?.margin_percent?.toFixed(1)}% margin
                </p>
              </div>
              <div className={`p-3 rounded-full ${(plData?.profit?.net || 0) >= 0 ? 'bg-blue-500/10' : 'bg-orange-500/10'}`}>
                <DollarSign className={`w-6 h-6 ${(plData?.profit?.net || 0) >= 0 ? 'text-blue-400' : 'text-orange-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projected Monthly Recurring */}
        <Card className="border-purple-500/20 bg-purple-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Monthly Recurring</p>
                <p className="text-2xl font-bold text-purple-400" data-testid="monthly-recurring">
                  {formatCurrency(plData?.projections?.monthly_recurring_income)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Projected recurring income
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-500/10">
                <RefreshCw className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend Chart */}
      <Card data-testid="trend-chart-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Monthly Trend
          </CardTitle>
          <CardDescription>Income vs Expenses over the last 6 months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-end gap-2">
            {trendData.map((month, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex items-end justify-center gap-1 h-48">
                  {/* Income bar */}
                  <div 
                    className="w-1/3 bg-emerald-500/80 rounded-t transition-all hover:bg-emerald-500"
                    style={{ 
                      height: `${Math.max((month.income / maxChartValue) * 100, 2)}%` 
                    }}
                    title={`Income: ${formatCurrency(month.income)}`}
                  />
                  {/* Expense bar */}
                  <div 
                    className="w-1/3 bg-red-500/80 rounded-t transition-all hover:bg-red-500"
                    style={{ 
                      height: `${Math.max((month.expenses / maxChartValue) * 100, 2)}%` 
                    }}
                    title={`Expenses: ${formatCurrency(month.expenses)}`}
                  />
                </div>
                <span className="text-xs text-muted-foreground">{month.month_label}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500" />
              <span>Income</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-red-500" />
              <span>Expenses</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Breakdown Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income by Source */}
        <Card data-testid="income-breakdown-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-400">
              <Wallet className="w-5 h-5" />
              Income by Source
            </CardTitle>
            <CardDescription>
              {plData?.income?.transaction_count || 0} income transactions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {Object.entries(plData?.income?.by_source || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(plData?.income?.by_source || {})
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 6)
                  .map(([source, amount]) => {
                    const percent = ((amount / (plData?.income?.total || 1)) * 100).toFixed(0);
                    return (
                      <div key={source}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm truncate max-w-[200px]">{source}</span>
                          <span className="text-sm font-medium">{formatCurrency(amount)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Wallet className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No income recorded for this period</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expenses by Category */}
        <Card data-testid="expense-breakdown-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <Receipt className="w-5 h-5" />
              Expenses by Category
            </CardTitle>
            <CardDescription>
              {plData?.expenses?.transaction_count || 0} expense transactions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {Object.entries(plData?.expenses?.by_category || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(plData?.expenses?.by_category || {})
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 6)
                  .map(([category, amount]) => {
                    const percent = ((amount / (plData?.expenses?.total || 1)) * 100).toFixed(0);
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm truncate max-w-[200px]">{category}</span>
                          <span className="text-sm font-medium">{formatCurrency(amount)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-500 rounded-full transition-all"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No expenses recorded for this period</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Mileage Deductions */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-cyan-500/10">
                <Car className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Mileage Deduction</p>
                <p className="text-xl font-bold" data-testid="mileage-deduction">
                  {formatCurrency(plData?.deductions?.mileage)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {plData?.deductions?.miles_driven?.toLocaleString()} miles driven
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Outstanding Invoices */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-amber-500/10">
                <FileText className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Outstanding Invoices</p>
                <p className="text-xl font-bold text-amber-400" data-testid="outstanding-invoices">
                  {formatCurrency(plData?.invoicing?.outstanding)}
                </p>
                <p className="text-xs text-muted-foreground">
                  of {formatCurrency(plData?.invoicing?.total_invoiced)} invoiced
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paid Invoices */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-emerald-500/10">
                <DollarSign className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Paid Invoices</p>
                <p className="text-xl font-bold text-emerald-400" data-testid="paid-invoices">
                  {formatCurrency(plData?.invoicing?.total_paid)}
                </p>
                <p className="text-xs text-muted-foreground">
                  collected this period
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ProfitLoss;
